import {
    EventrDate,
    IField,
    IValidation,
    ValidationTypes,
    getJsonKeyFromField,
    getTimezoneName,
} from '@eventr/common'
import {
    DateTimePicker,
    findParentObjectById,
    useValidatorContext,
} from '@eventr/common-storybook'
import { useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { useLanguageContext } from '../../../state/languageState/languageContext'
import { useEditSectionContext } from '../editSectionProvider'
interface IDateTimeEditFieldProps {
    field: IField
    _id: string
}

export const DateTimeEditField = ({ field, _id }: IDateTimeEditFieldProps) => {
    const { getValue, values, updateValues, deleteSetValue } =
        useEditSectionContext()
    const { isFieldValid, removeFieldState } = useValidatorContext()
    const { getEventInfoV2, getFieldById } = useBoundStore(
        useShallow((s) => {
            return {
                getEventInfoV2: s.getEventInfoV2,
                getFieldById: s.getFieldById,
            }
        })
    )
    const { getLanguageISO639Code } = useLanguageContext()

    const [state, setState] = useState<string | undefined>(undefined)
    const [isError, setIsError] = useState<boolean>(false)
    const [textError, setTextError] = useState<string>('')

    useEffect(() => {
        const initFieldValue = getValue(field, _id)
        if (
            typeof initFieldValue !== 'string' &&
            initFieldValue !== null &&
            typeof initFieldValue !== 'number'
        ) {
            setState(JSON.stringify(initFieldValue))
        } else {
            setState(initFieldValue ?? undefined)
        }
    }, [field, _id, getValue])

    useEffect(() => {
        if (values) {
            const valid = isFieldValid(field, _id, state, values)

            if ((valid !== true) !== isError) {
                setIsError(valid !== true)
            }
            if (valid !== true) {
                setTextError(valid as string)
            } else {
                setTextError('')
            }
        }
    }, [state, field, _id, isFieldValid, setIsError, isError, setTextError])

    const cleanUp = () => {
        removeFieldState(field, _id)
    }

    useEffect(() => {
        return () => {
            cleanUp()
        }
    }, [])

    const isRequired = useMemo(() => {
        return field.type_properties?.validations?.some(
            (v) => v.type === ValidationTypes.required
        )
    }, [field.type_properties?.validations])

    const buildPathToField = (fieldId: number) => {
        const path: string[] = []
        const field = getFieldById(fieldId)
        if (!field) return path
        const fieldJson = getJsonKeyFromField(field)
        path.push(fieldJson)

        let parent: number | undefined = field?.parent
        while (parent) {
            const json = getJsonKeyFromField(getFieldById(parent)!)

            path.push(json)
            parent = getFieldById(parent)?.parent
        }
        return path.reverse()
    }

    /// To limit the selection of the field.
    /// undifined will mean no limit.
    function getMaxMinDateIfExists(
        validationType: ValidationTypes
    ): EventrDate | undefined {
        try {
            var dateValidationObject: IValidation =
                field.type_properties.validations?.find((validationObject) => {
                    if (validationObject.type === validationType) {
                        return true
                    } else {
                        return false
                    }
                })
            if (dateValidationObject) {
                if (values.value) {
                    const parent = findParentObjectById(values.value, _id)

                    const stringPathList: string[] = buildPathToField(
                        dateValidationObject.fieldValue
                    )

                    var willBeEndValue = parent

                    for (const pathPart of stringPathList) {
                        if (typeof willBeEndValue === 'object') {
                            if (willBeEndValue[pathPart]) {
                                willBeEndValue = willBeEndValue[pathPart]
                            } else {
                                willBeEndValue = willBeEndValue[0]
                                willBeEndValue = willBeEndValue[pathPart]
                            }
                        }
                    }

                    if (willBeEndValue) {
                        const rangeDate = new EventrDate(willBeEndValue)
                        return rangeDate
                    } else {
                        return undefined
                    }
                } else {
                    return undefined
                }
            } else {
                return undefined
            }
        } catch {
            return undefined
        }
    }

    function onChange(date: string) {
        if (date !== undefined) {
            let dateToUse = date

            /// State hasent been set before...
            if (!state) {
                const tmpDate = new EventrDate(date)
                tmpDate.setFromObject({ hour: 12 })
                dateToUse = tmpDate.dateTimeString
            }

            const currEventrDate = new EventrDate(state)
            const eventrDateToUse = new EventrDate(dateToUse)
            if (!currEventrDate.timezoneNumber) {
                eventrDateToUse.setTimezone(
                    getTimezoneName(currEventrDate.timezoneNumber)
                )
            }
            setState(dateToUse)
            updateValues({
                value: eventrDateToUse.dateTimeString,
                field: field,
                _id: _id,
            })
        } else {
            /// deal with onclear
            setState(date)
            deleteSetValue({ value: date, field: field, _id: _id })
        }
    }
    return (
        <DateTimePicker
            id={`${field.field_name}-selector`}
            eventrDateString={state}
            dateHeaderTitle={i18n.t('edit-itinerary.fields.select-date')}
            timeHeaderTitle={i18n.t('edit-itinerary.fields.select-time')}
            required={isRequired}
            isError={isError}
            outOfRangePopup
            textError={textError}
            onChange={onChange}
            fieldLabel={i18n.t(`edit-itinerary.fields.${field.id}`)}
            initialState={
                getMaxMinDateIfExists(
                    ValidationTypes.afterDate
                )?.toItinLocalDateString() ?? getEventInfoV2().data.start_date
            }
            minDate={
                getMaxMinDateIfExists(ValidationTypes.afterDate) ??
                new EventrDate(getEventInfoV2().data.start_date)
            }
            maxDate={new EventrDate(getEventInfoV2().data.end_date)}
            extendMinMaxByDays={1}
            locale={getLanguageISO639Code()}
            saveText={i18n.t('general.done')}
            clearText={i18n.t('general.clear')}
            minuteText={i18n.t('general.minute')}
            hourText={i18n.t('general.hour')}
            selectDateText={i18n.t('edit-itinerary.fields.select-date')}
            selectTimeText={i18n.t('edit-itinerary.fields.select-time')}
            highlightRangeStart={getEventInfoV2().data.start_date}
            highlightRangeEnd={getEventInfoV2().data.end_date}
        />
    )
}
